import React, { useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import Checkbox from "@components/Checkbox";
import Text from "@components/Text";
import { useStores } from "@stores";
import { useLang } from "@src/hooks/useLang";
import { TDevice, TDeviceFarms } from "@services/devicesService";
import { useDevicesScreenVM } from "@screens/DevicesScreen/DevicesScreenVM";

interface IProps {
  device: TDevice;
}

// Stable pastel palette; a farm keeps its color by position in the farm list.
// Shared with the farm filter pills in DevicesListHeader.
export const TAG_COLORS: Array<{ background: string; color: string }> = [
  { background: "#EDE7FD", color: "#6D49E8" },
  { background: "#FDE7F4", color: "#D9309F" },
  { background: "#FCE9E4", color: "#E2542C" },
  { background: "#E7EDFD", color: "#3554D1" },
  { background: "#E4F6EC", color: "#1F9254" },
];

const Root = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  & > * {
    margin-right: 8px;
    margin-bottom: 8px;
  }
`;

const Tag = styled.div<{ background?: string; color?: string }>`
  font-family: Nunito Sans, sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  line-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: 24px;
  padding: 0 12px;
  background: ${({ background }) => background ?? "#F8F8F8"};
  color: ${({ color }) => color ?? "#494C5B"};
  border-radius: 4px;
  white-space: nowrap;
`;

const PlusButton = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 4px;
  color: #494c5b;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: 0.4s;
  :hover {
    background: #fff;
  }
`;

const Popover = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 20;
  min-width: 220px;
  background: #fff;
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 8px 12px;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 19;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  cursor: pointer;
`;

const DeviceFarmTags: React.FC<IProps> = ({ device }) => {
  const lang = useLang();
  const t = (lang.devices ?? {}) as any;
  const allFarmsLabel = ((lang as any).farms?.allFarms as string) ?? "All farms";
  const vm = useDevicesScreenVM();
  const { accountStore, farmStore } = useStores();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const farms: TDeviceFarms = device.farms ?? { allFarms: true, farmIds: [] };
  const canManage = !accountStore.isMember;

  const colorOf = (farmId: string) => {
    const idx = farmStore.farms.findIndex((f) => f.id === farmId);
    return TAG_COLORS[(idx >= 0 ? idx : 0) % TAG_COLORS.length];
  };

  const save = async (next: TDeviceFarms) => {
    if (saving) return;
    setSaving(true);
    try {
      await vm.saveDeviceFarms(device.id, next);
    } finally {
      setSaving(false);
    }
  };

  const toggleAllFarms = () =>
    save(
      farms.allFarms
        ? { allFarms: false, farmIds: farms.farmIds }
        : { allFarms: true, farmIds: [] }
    );

  const toggleFarm = (farmId: string) => {
    const next = farms.farmIds.includes(farmId)
      ? farms.farmIds.filter((id) => id !== farmId)
      : [...farms.farmIds, farmId];
    save({ allFarms: false, farmIds: next });
  };

  return (
    <Root onClick={(e) => e.stopPropagation()}>
      {canManage && (
        <PlusButton className="plus-button" onClick={() => setOpen(!open)}>
          +
        </PlusButton>
      )}
      {farms.allFarms ? (
        <Tag>{allFarmsLabel}</Tag>
      ) : (
        farms.farmIds.map((farmId) => {
          const farm = farmStore.farms.find((f) => f.id === farmId);
          if (!farm) return null;
          const c = colorOf(farmId);
          return (
            <Tag key={farmId} background={c.background} color={c.color}>
              {farm.name}
            </Tag>
          );
        })
      )}
      {open && (
        <>
          <Overlay onClick={() => setOpen(false)} />
          <Popover style={saving ? { opacity: 0.6, pointerEvents: "none" } : {}}>
            <Text size={12} color="#899093" type="heading">
              {t.deviceFarmsTitle ?? "Visible to farms"}
            </Text>
            <OptionRow onClick={toggleAllFarms}>
              <Text size={14} color="#494C5B">
                {t.deviceAllFarms ?? "All farms (including future ones)"}
              </Text>
              <Checkbox checked={farms.allFarms} />
            </OptionRow>
            {farmStore.farms.map((f) => (
              <OptionRow key={f.id} onClick={() => toggleFarm(f.id)}>
                <Text size={14} color="#494C5B">
                  {f.name}
                </Text>
                <Checkbox checked={!farms.allFarms && farms.farmIds.includes(f.id)} />
              </OptionRow>
            ))}
          </Popover>
        </>
      )}
    </Root>
  );
};

export default observer(DeviceFarmTags);
